import Link from "next/link";
import { ArrowUpRight, type LucideIcon } from "lucide-react";

/**
 * The app's one empty-state pattern — extracted from the /saved "nothing
 * saved" card so every catalog/list surface reads the same when it has no
 * content: soft card, centered accent hairline, icon chip (echo the page's
 * left-nav icon), bold line, optional hint and call-to-action pill. All
 * colours are theme tokens, so it re-themes per portal.
 */
export function EmptyState({
  Icon,
  title,
  hint,
  cta,
}: {
  Icon: LucideIcon;
  title: string;
  hint?: string;
  cta?: { href: string; label: string };
}) {
  return (
    <div
      className="relative overflow-hidden rounded-card bg-card p-12 text-center"
      style={{ boxShadow: "var(--shadow-soft)" }}
    >
      <span
        aria-hidden
        className="absolute inset-x-12 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, color-mix(in srgb, var(--color-accent) 45%, transparent) 50%, transparent)",
        }}
      />
      <span className="grid place-items-center size-12 rounded-2xl bg-muted text-foreground/55 mx-auto mb-4">
        <Icon className="size-5" aria-hidden />
      </span>
      <p className="font-display font-bold text-foreground text-[18px]">{title}</p>
      {hint ? (
        <p className="text-[13px] text-muted-foreground mt-2 max-w-sm mx-auto">{hint}</p>
      ) : null}
      {cta ? (
        <Link
          href={cta.href}
          className="inline-flex items-center gap-1.5 mt-5 h-10 px-5 rounded-full bg-primary text-primary-foreground text-[12px] font-display font-bold uppercase tracking-[0.14em] hover:bg-primary/90 transition-colors"
        >
          {cta.label}
          <ArrowUpRight className="size-3.5" aria-hidden />
        </Link>
      ) : null}
    </div>
  );
}
